import { BlobProvider } from '@react-pdf/renderer';
import { useRef, type FC, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import {
  graphql,
  RelayEnvironmentProvider,
  useFragment,
  useRelayEnvironment,
} from 'react-relay';

import { i18n } from '@woovi/i18n';
import { CHARGE_STATUS_ENUM } from '@woovi/shared';

import type { ChargePrintPDF_charge$key } from './__generated__/ChargePrintPDF_charge.graphql.ts';
import PaymentActionsPDFDocument from './ChargePDFDocument.tsx';

type ChargePrintPDFTriggerProps = {
  onPrint: () => void;
  disabled: boolean;
};

type ChargePrintPDFProps = {
  charge: ChargePrintPDF_charge$key;
  trigger: FC<ChargePrintPDFTriggerProps>;
};

const ChargePrintPDF = (props: ChargePrintPDFProps): ReactNode => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const charge = useFragment<ChargePrintPDF_charge$key>(
    graphql`
      fragment ChargePrintPDF_charge on Charge {
        status
        ...ChargePDFDocument_charge
      }
    `,
    props.charge,
  );

  const handlePrint = (): void => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.focus();
      iframeRef.current.contentWindow?.print();
    }
  };

  const environment = useRelayEnvironment();

  const documentWithProviders = (
    <RelayEnvironmentProvider environment={environment}>
      <I18nextProvider i18n={i18n}>
        <PaymentActionsPDFDocument charge={charge} />
      </I18nextProvider>
    </RelayEnvironmentProvider>
  );

  const Trigger = props.trigger;

  return (
    <BlobProvider document={documentWithProviders}>
      {({ url, loading, error }) => {
        const hasUrl = !!url;
        const hasError = !!error;
        const isLoading = loading;

        const shouldRenderIframe = !isLoading && !hasError && hasUrl;
        const isDisabled =
          isLoading || hasError || charge.status !== CHARGE_STATUS_ENUM.ACTIVE;

        return (
          <>
            {shouldRenderIframe && (
              <iframe ref={iframeRef} src={url} style={{ display: 'none' }} />
            )}
            <Trigger disabled={isDisabled} onPrint={handlePrint} />
          </>
        );
      }}
    </BlobProvider>
  );
};

export default ChargePrintPDF;
